/**
 * The sentence that will one day open a locked passage.
 *
 * It is chosen when the passage is marked and then never shown again — which means a
 * forgotten sentence used to be unrecoverable, and the passage unpublishable, with
 * nothing in the editor admitting it. This is where the author reads it back and
 * changes it.
 *
 * The book is re-read when the dialog opens rather than trusted from page load: the
 * sentence may have been set minutes ago, on a copy this component never saw.
 */

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'

import { apiService } from '../../services/api'
import { logger } from '../../utils/logger'

interface LockPhraseDialogProps {
  open: boolean
  bookId: string
  encryptionId: string
  onClose: () => void
}

export const LockPhraseDialog: React.FC<LockPhraseDialogProps> = ({ open, bookId, encryptionId, onClose }) => {
  const [phrase, setPhrase] = useState('')
  const [loading, setLoading] = useState(true)
  const [hadPhrase, setHadPhrase] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) {
      return
    }
    setLoading(true)
    setPhrase('')
    setHadPhrase(false)

    let cancelled = false
    apiService
      .getBook(bookId)
      .then((res) => {
        if (cancelled) {
          return
        }
        const stored = res.data?.settings?.lockPhrases?.[encryptionId] ?? ''
        setPhrase(stored)
        setHadPhrase(Boolean(stored))
      })
      .catch((err) => {
        if (!cancelled) {
          logger.error('Failed to load the lock phrase', err)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [open, bookId, encryptionId])

  const handleSave = async () => {
    const trimmed = phrase.trim()
    if (!trimmed) {
      return
    }
    setSaving(true)
    try {
      await apiService.saveLockPhrase(bookId, encryptionId, trimmed)
      toast.success('המשפט נשמר')
      onClose()
    } catch (err: any) {
      toast.error(err?.message || 'לא הצלחנו לשמור את המשפט')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth dir="rtl">
      <DialogTitle sx={{ textAlign: 'right' }}>המשפט שפותח את הקטע</DialogTitle>
      <DialogContent>
        {loading ? (
          <Stack alignItems="center" sx={{ py: 3 }}>
            <CircularProgress size={24} />
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            {!hadPhrase && (
              <Alert severity="warning">
                לקטע הזה לא נשמר משפט. בלעדיו אי אפשר לפרסם אותו — הקוראים יקבלו מקום ריק.
              </Alert>
            )}
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={2}
              label="המשפט"
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              helperText="שינוי המשפט מחייב פרסום מחדש של הקטע, אחרת הגליפים נשארים נעולים בישן."
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ flexDirection: 'row-reverse', justifyContent: 'flex-start', gap: 1, px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          ביטול
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={loading || saving || !phrase.trim()}>
          שמירה
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default LockPhraseDialog
